import React, { useCallback, useMemo, useState } from "react";
import { BaseModal, IModalInstance } from "@whatslly/ui-components/modals";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import styles from "./ChatAddLabelsModal.module.scss";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { IMChat } from "@whatslly/ui-components/Pages/TuvisMessaging/services/IMChat";
import countBy from "lodash/countBy";
import { CheckBox } from "@whatslly/ui-components/v2/BaseElements/CheckBox";

type ModalResType = { labelsToAdd: string[]; labelsToRemove: string[] };

interface IChatAddLabelsModalProps extends IModalInstance<ModalResType> {
  chats: IMChat[];
  numberOfSelectedChats?: number;
  labels: Record<string, string>;
}

export const ChatAddLabelsModal: React.FC<IChatAddLabelsModalProps> = observer(({ closeModal, chats, numberOfSelectedChats, labels }) => {
  const { t } = useTranslation();
  const initialChatsLabels = useMemo(() => chats.flatMap((chat) => chat.labels).filter(Boolean), [chats]);
  const initialChatsLabelsCount = useMemo(() => countBy(initialChatsLabels), [initialChatsLabels]);
  const initialLabelsState = useMemo<
    Record<
      string,
      {
        labelName: string;
        state: "active" | "partial-active" | "not-active";
      }
    >
  >(() => {
    const formattedLabelsAsEntries = Object.entries(labels).map(([labelId, labelName]) => [
      labelId,
      {
        labelName,
        state: initialChatsLabelsCount[labelId]
          ? initialChatsLabelsCount[labelId] === chats.length
            ? ("active" as const)
            : ("partial-active" as const)
          : ("not-active" as const),
      },
    ]);
    return Object.fromEntries(formattedLabelsAsEntries);
  }, [chats.length, initialChatsLabelsCount, labels]);
  const [labelsState, setLabelsState] = useState(initialLabelsState);
  const [res, setRes] = useState<{ labelsToAdd: string[]; labelsToRemove: string[] }>({ labelsToAdd: [], labelsToRemove: [] });

  const handleOnChange = useCallback(
    (labelId: string) => {
      const currentLabelState = labelsState[labelId];
      if (currentLabelState?.state === "not-active") {
        setLabelsState((prevState) => ({ ...prevState, [labelId]: { ...currentLabelState, state: "active" } }));
        setRes((prev) => ({
          labelsToAdd: [...prev.labelsToAdd, labelId],
          labelsToRemove: prev.labelsToRemove.filter((labelToRemoveId) => labelToRemoveId !== labelId),
        }));
        return;
      }
      setLabelsState((prevState) => ({ ...prevState, [labelId]: { ...currentLabelState, state: "not-active" } }));
      setRes((prev) => {
        return {
          labelsToAdd: prev.labelsToAdd.filter((labelToAddId) => labelToAddId !== labelId),
          labelsToRemove:
            currentLabelState?.state === "partial-active" || initialLabelsState[labelId]?.state === "active"
              ? [...prev.labelsToRemove, labelId]
              : prev.labelsToRemove,
        };
      });
    },
    [labelsState]
  );

  return (
    <BaseModal
      backdropClassName={styles.backdrop}
      testId="chat-modify-labels-modal"
      closeModal={closeModal}
      header={t("whatsapp_business_api_agent_page.labels.modal.header")}
      closeOnEscape={false}
      closeOnClickOutside={false}
      className={styles.modal}
      bodyClassName={styles.body}
      footer={<ModalButtons testId="modal-buttons-submit-button" onCancel={() => closeModal()} onSubmit={() => closeModal(res)} />}
    >
      <div className={styles.main}>
        {(numberOfSelectedChats || 0) > 1 && (
          <div>
            {t("whatsapp_business_api_agent_page.selected_chats")} - {numberOfSelectedChats}
          </div>
        )}
        <div className={styles.labelsContainer}>
          {Object.entries(labelsState).map(([labelId, { labelName, state }]) => (
            <div className={styles.label} key={`${labelId}-${state}`}>
              <span>{labelName}</span>
              <div>
                <CheckBox
                  className={state === "partial-active" ? styles.minusCustomStyle : undefined}
                  iconColor={"main-secondary"}
                  data-testid={`chat-label-state-${state}`}
                  icon={state === "partial-active" ? "minus-thick" : undefined}
                  checked={state === "active" || state === "partial-active"}
                  onChange={() => handleOnChange(labelId)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </BaseModal>
  );
});
